import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bookingPolicyApi,
  bookingPolicyQueryKeys,
  type TimeMode,
} from "../../shared/api/bookingPolicy";
import { clinicSessionsApi, clinicSessionsQueryKeys } from "../../shared/api/clinicSessions";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SLOT_MINUTE_OPTIONS, slotCount, slotStarts } from "./sessionSlots";

/**
 * #19935 — Hình thức giờ, đặt ở ĐẦU trang Buổi khám: thứ tự đọc là Giờ làm việc → Hình thức giờ →
 * Buổi khám. Buổi khám dùng ở CẢ HAI chế độ; hình thức giờ chỉ đổi độ mịn — Theo buổi thì bệnh
 * nhân chọn buổi, Theo giờ thì khung giờ được cắt BÊN TRONG các buổi bên dưới.
 *
 * Vẫn lưu ở chính sách đặt lịch (`/booking-policy`, hợp đồng gateway không đổi); chỉ chỗ chỉnh dời
 * về đây. Dùng chung queryKey với trang Luồng & phân công và trang Giới hạn, nên xem trước luồng
 * và đơn vị sức chứa ở hai trang đó cập nhật theo (B3-04).
 *
 * #19979 — số phút chọn từ danh sách (ô số tự do nhận cả 1 phút); xem trước số khung lấy từ các
 * khoảng giờ đang nhận lịch; đổi CHẾ ĐỘ phải xác nhận vì nó đổi ngay thứ bệnh nhân thấy.
 */
export function TimeModeCard() {
  const qc = useQueryClient();
  const policyQ = useQuery({
    queryKey: bookingPolicyQueryKeys.all(),
    queryFn: () => bookingPolicyApi.get(),
  });
  // Cùng queryKey với danh sách trên trang: không gọi thêm lần nào.
  const sessionsQ = useQuery({
    queryKey: clinicSessionsQueryKeys.all(),
    queryFn: () => clinicSessionsApi.list(),
  });
  const [timeMode, setTimeMode] = useState<TimeMode>("SESSION");
  const [slotMinutes, setSlotMinutes] = useState(15);
  const [confirming, setConfirming] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!policyQ.data) return;
    setTimeMode(policyQ.data.timeMode);
    setSlotMinutes(policyQ.data.slotMinutes);
  }, [policyQ.data]);

  const p = policyQ.data;
  const dirty = !!p && (p.timeMode !== timeMode || p.slotMinutes !== slotMinutes);
  const modeChanged = !!p && p.timeMode !== timeMode;
  // Giá trị cũ ngoài danh sách vẫn hiện đúng như đang lưu, kèm lời nhắc — không tự đổi thay người.
  const legacyMinutes = !(SLOT_MINUTE_OPTIONS as readonly number[]).includes(slotMinutes);

  const active = (sessionsQ.data ?? [])
    .filter((s) => s.active)
    .sort((a, b) => a.startTime.localeCompare(b.startTime));
  const totalSlots = active.reduce(
    (n, s) => n + slotCount(s.startTime, s.endTime, slotMinutes),
    0,
  );
  const firstStarts = active.flatMap((s) => slotStarts(s.startTime, s.endTime, slotMinutes));

  const save = useMutation({
    mutationFn: () => bookingPolicyApi.update({ ...p!, timeMode, slotMinutes }),
    onSuccess: (saved) => {
      qc.setQueryData(bookingPolicyQueryKeys.all(), saved);
      setMsg("Đã lưu cách chọn giờ.");
      setError(null);
      setConfirming(false);
    },
    onError: (e: unknown) => {
      setError(apiError(e).detail);
      setMsg(null);
      setConfirming(false);
    },
  });

  function choose(mode: TimeMode) {
    setTimeMode(mode);
    setMsg(null);
  }

  return (
    <Card className="mb-4">
      <CardContent className="grid gap-3 pt-6">
        <div>
          {/* #19977 — nói thẳng thứ bị đổi: cách BỆNH NHÂN chọn giờ (Mini App, website). */}
          <h2 className="m-0 text-base font-semibold">Bệnh nhân chọn giờ theo</h2>
          <p className="m-0 text-sm text-muted-foreground">
            Áp cho các khung giờ nhận lịch bên dưới, trên Mini App và website.
          </p>
        </div>
        <fieldset className="grid gap-2 text-sm sm:grid-cols-2">
          <legend className="sr-only">Bệnh nhân chọn giờ theo</legend>
          <label
            className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 ${
              timeMode === "SESSION" ? "border-primary bg-primary/5" : "border-input"
            }`}
          >
            <input
              type="radio"
              name="timeMode"
              aria-label="Buổi (Sáng, Chiều…)"
              checked={timeMode === "SESSION"}
              onChange={() => choose("SESSION")}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <span>
              <span className="font-medium">Buổi (Sáng, Chiều…)</span>
              <span className="block text-xs text-muted-foreground">
                {active.length > 0
                  ? `Bệnh nhân chọn 1 trong ${active.length} buổi: ${active.map((s) => s.label).join(", ")}.`
                  : "Bệnh nhân chọn cả buổi, không chọn giờ cụ thể."}
              </span>
            </span>
          </label>
          <label
            className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 ${
              timeMode === "SLOT" ? "border-primary bg-primary/5" : "border-input"
            }`}
          >
            <input
              type="radio"
              name="timeMode"
              aria-label="Giờ cụ thể"
              checked={timeMode === "SLOT"}
              onChange={() => choose("SLOT")}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <span className="grid gap-1">
              <span className="font-medium">Giờ cụ thể</span>
              {timeMode === "SLOT" && (
                <span className="flex items-center gap-2">
                  <span>mỗi khung</span>
                  <select
                    aria-label="Số phút mỗi khung giờ"
                    value={slotMinutes}
                    onChange={(e) => {
                      setSlotMinutes(Number(e.target.value));
                      setMsg(null);
                    }}
                    className="h-8 rounded-md border border-input bg-background px-2"
                  >
                    {legacyMinutes && <option value={slotMinutes}>{slotMinutes}</option>}
                    {SLOT_MINUTE_OPTIONS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <span>phút</span>
                </span>
              )}
              <span className="block text-xs text-muted-foreground" data-testid="slot-preview">
                {active.length === 0
                  ? "Bệnh nhân chọn giờ cụ thể, cắt đều trong các khoảng bên dưới."
                  : `${firstStarts.slice(0, 3).join(" · ")}${firstStarts.length > 3 ? " …" : ""} (${totalSlots} khung/ngày)`}
              </span>
              {timeMode === "SLOT" && legacyMinutes && (
                <span className="block text-xs text-amber-700 dark:text-amber-300">
                  {slotMinutes} phút là giá trị cũ ngoài danh sách gợi ý; vẫn dùng được, nên chọn
                  một giá trị trong danh sách.
                </span>
              )}
            </span>
          </label>
        </fieldset>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => (modeChanged ? setConfirming(true) : save.mutate())}
          >
            {save.isPending ? "Đang lưu…" : "Lưu cách chọn giờ"}
          </Button>
          {msg && <p className="m-0 text-sm text-emerald-600">{msg}</p>}
          {error && <p className="m-0 text-sm text-destructive">{error}</p>}
        </div>
        {/* #19977 — sức chứa mỗi khung là luật cố định của engine, không có ô nào để chỉnh; nói ra
            để người chuyển sang Giờ cụ thể không đi tìm "giới hạn mỗi khung". */}
        <p className="m-0 text-xs text-muted-foreground" data-testid="time-mode-hint">
          {timeMode === "SLOT"
            ? "Mỗi khung chia đều từ giờ bắt đầu của từng khoảng bên dưới. Mỗi khung, mỗi phòng nhận 1 bệnh nhân (nhiều bác sĩ cùng trực thì nhận bằng số bác sĩ)."
            : "Bệnh nhân chọn cả buổi (VD: Sáng 07:00–11:30), không chọn giờ cụ thể."}
        </p>
      </CardContent>

      <Dialog open={confirming} onOpenChange={(open) => !open && setConfirming(false)}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              Đổi sang chọn theo {timeMode === "SLOT" ? "giờ cụ thể" : "buổi"}?
            </DialogTitle>
            <DialogDescription>Thay đổi có hiệu lực ngay sau khi lưu.</DialogDescription>
          </DialogHeader>
          <ul className="m-0 grid list-disc gap-1 pl-5 text-sm">
            <li>
              Bệnh nhân trên Mini App và website sẽ{" "}
              {timeMode === "SLOT"
                ? `chọn giờ cụ thể (khung ${slotMinutes} phút)`
                : "chọn cả buổi, không chọn giờ"}
              . Lịch đã đặt giữ nguyên.
            </li>
            <li>
              Giới hạn "lượt mỗi buổi" vẫn tính trên cả buổi. Xem lại ở{" "}
              <a href="/settings/booking-limits" className="text-primary underline">
                Giới hạn &amp; nhắc lịch
              </a>
              .
            </li>
          </ul>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirming(false)}>
              Huỷ
            </Button>
            <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Đang lưu…" : "Đổi và lưu"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
